<template>
  <div class="yd-chat" role="dialog" aria-label="YourDoc assistant">
    <header class="yd-chat__header">
      <span class="yd-chat__avatar"><i class="fas fa-robot"></i></span>
      <div class="yd-chat__title">
        <strong>YourDoc Assistant</strong>
        <span>Find doctors, check slots, booking help</span>
      </div>
      <button type="button" class="yd-chat__icon" title="New chat" aria-label="Start a new chat" @click="newChat">
        <i class="fas fa-redo-alt"></i>
      </button>
      <button type="button" class="yd-chat__icon" title="Close" aria-label="Close chat" @click="$emit('close')">
        <i class="fas fa-times"></i>
      </button>
    </header>

    <deep-chat
      ref="chat"
      :key="chatKey"
      class="yd-chat__body"
      :connect="connect"
      :requestBodyLimits="requestBodyLimits"
      :history="initialHistory"
      :introMessage="introMessage"
      :textInput="textInput"
      :messageStyles="messageStyles"
      :submitButtonStyles="submitButtonStyles"
      :chatStyle="chatStyle"
      :auxiliaryStyle="auxiliaryStyle"
      @new-message="onNewMessage"
    ></deep-chat>

    <p class="yd-chat__note">AI answers can be wrong. For emergencies call 112 / 108.</p>
  </div>
</template>

<script>
import "deep-chat";
import { markRaw } from "vue";

// Kept for this browser tab only: health questions should not outlive the session.
const HISTORY_KEY = "yourdoc.chat.history";
const MAX_SAVED = 30;

function loadHistory() {
  try {
    return JSON.parse(sessionStorage.getItem(HISTORY_KEY)) || [];
  } catch (e) {
    return [];
  }
}

function saveHistory(history) {
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-MAX_SAVED)));
  } catch (e) {
    // storage blocked or full: the chat just won't survive a reload
  }
}

// "real" when the API runs on a server that can stream (uvicorn / Docker);
// anything else suits the Lambda deployment.
const REAL_STREAM = process.env.VUE_APP_CHAT_STREAM === "real";

const SUGGESTIONS = [
  "Find a dermatologist near me",
  "How do I book an appointment?",
  "How do I add a family member?",
];

export default {
  name: "Chat",
  emits: ["close"],
  data() {
    // deep-chat structuredClone()s its settings, which fails on Vue's reactive
    // proxies - so everything handed to it is markRaw'd.
    return {
      chatKey: 0,
      // What the widget starts with; later messages are tracked in this.saved
      // (re-binding history on every message would make the widget reload).
      initialHistory: markRaw(loadHistory()),
      connect: markRaw(REAL_STREAM
        ? {
            // Server-sent events: words appear as the model writes them.
            url: `${process.env.VUE_APP_API_URL}/chat/stream`,
            method: "POST",
            headers: { "api-key": process.env.VUE_APP_API_KEY },
            stream: true,
          }
        : {
            // AWS Lambda (Mangum) buffers responses, so a real stream would still
            // arrive all at once: get the full answer and type it out instead.
            url: `${process.env.VUE_APP_API_URL}/chat`,
            method: "POST",
            headers: { "api-key": process.env.VUE_APP_API_KEY },
            stream: { simulation: 12 },
          }),
      introMessage: markRaw({
        html: `<div class="yd-intro">
            <p><b>Hi! I'm the YourDoc assistant.</b><br>Tell me your symptoms or the specialist you need and I'll find doctors and open slots for you.</p>
            ${SUGGESTIONS.map(s => `<button class="deep-chat-button deep-chat-suggestion-button yd-suggest">${s}</button>`).join("")}
          </div>`,
      }),
      textInput: markRaw({
        placeholder: { text: "Ask about doctors, slots or booking…" },
        styles: {
          container: { borderRadius: "10px", border: "1px solid #d0d5dd", boxShadow: "none", width: "82%", backgroundColor: "#fff" },
          text: { padding: "10px 12px", fontSize: "14px" },
        },
        characterLimit: 2000,
      }),
      messageStyles: markRaw({
        default: {
          shared: { bubble: { maxWidth: "88%", borderRadius: "14px", padding: "9px 12px", fontSize: "14px", lineHeight: "1.45" } },
          user: { bubble: { backgroundColor: "#0e82fd", color: "#fff" } },
          ai: { bubble: { backgroundColor: "#f2f4f7", color: "#101828" } },
        },
        intro: { bubble: { backgroundColor: "transparent", maxWidth: "100%", padding: "0" } },
        loading: { message: { styles: { bubble: { backgroundColor: "#f2f4f7" } } } },
      }),
      submitButtonStyles: markRaw({
        submit: {
          container: { default: { backgroundColor: "#0e82fd", borderRadius: "10px", padding: "6px", marginLeft: "6px" } },
          svg: { styles: { default: { filter: "brightness(0) invert(1)" } } },
        },
      }),
      chatStyle: markRaw({ width: "100%", height: "100%", border: "none", backgroundColor: "#fff", fontFamily: "inherit" }),
      requestBodyLimits: markRaw({ maxMessages: 10 }),
      auxiliaryStyle: `
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-thumb { background-color: #d0d5dd; border-radius: 3px; }
        a { color: #0b6fd8; font-weight: 600; }
        p { margin: 0 0 6px; } p:last-child { margin-bottom: 0; }
        ul, ol { margin: 4px 0 6px; padding-left: 18px; }
        .yd-intro p { margin: 0 0 10px; color: #344054; font-size: 14px; line-height: 1.45; }
        .yd-suggest { display: block; width: 100%; text-align: left; margin: 0 0 6px; padding: 8px 12px;
          border: 1px solid #b2ddff; background: #e8f3ff; color: #0b6fd8; border-radius: 10px; font-size: 13px; cursor: pointer; }
        .yd-suggest:hover { background: #d1e9ff; }`,
    };
  },
  created() {
    this.saved = loadHistory();
  },
  methods: {
    onNewMessage(event) {
      const { message, isHistory } = event.detail || {};
      if (isHistory || !message || !message.text) return;
      const role = message.role === "user" ? "user" : "ai";
      // Streamed answers arrive as one message once complete.
      this.saved.push({ role, text: message.text });
      saveHistory(this.saved);
    },
    newChat() {
      this.saved = [];
      this.initialHistory = markRaw([]);
      saveHistory([]);
      this.chatKey += 1; // a fresh widget with the intro and suggestions again
    },
  },
};
</script>

<style scoped>
.yd-chat {
  position: fixed;
  right: 20px;
  bottom: 84px;
  z-index: 20005; /* above the site header (1030) and the install banner (20001) */
  width: 370px;
  height: min(600px, calc(100vh - 190px)); /* stays clear of the top bars */
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 16px;
  box-shadow: 0 20px 40px -8px rgba(16, 24, 40, 0.25);
  overflow: hidden;
}

.yd-chat__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 12px 12px 14px;
  background: linear-gradient(135deg, #0e82fd, #0b6fd8);
  color: #fff;
}

.yd-chat__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.yd-chat__title {
  flex: 1;
  min-width: 0;
  line-height: 1.25;
}

.yd-chat__title strong {
  display: block;
  font-size: 15px;
}

.yd-chat__title span {
  display: block;
  font-size: 12px;
  opacity: 0.85;
}

.yd-chat__icon {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.yd-chat__icon:hover {
  background: rgba(255, 255, 255, 0.18);
}

.yd-chat__body {
  flex: 1;
  min-height: 0;
  display: block;
}

.yd-chat__note {
  margin: 0;
  padding: 6px 12px 8px;
  font-size: 11px;
  color: #98a2b3;
  text-align: center;
  border-top: 1px solid #f2f4f7;
}

@media (max-width: 480px) {
  .yd-chat {
    right: 8px;
    left: 8px;
    width: auto;
    bottom: 76px;
    height: calc(100vh - 96px);
  }
}
</style>
